import React, { useState, useEffect } from 'react';
import { Country, City } from 'country-state-city';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import Button from '../../shared/components/Button';
import StepsTracker from '../../components/StepsTracker/StepsTracker';
import clubbieLogo from '../../assets/images/clubbie_logo_full.svg';
import { editProfile } from '../../redux/actions/authActions';
import { AUTH_STACK, DASHBOARD_STACK } from '../../utils/screenNames';

/**
 * LocationDetails Page - Step 2 of signup
 * Refactored with Tailwind CSS to match Personal/Organisation layout
 */
function LocationDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const user = useSelector((state) => state.user);
  const profile = user?.user?.profile;

  // State
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load countries on mount
  useEffect(() => {
    const allCountries = Country.getAllCountries();
    setCountries(allCountries);
  }, []);

  // Prefill from route state (passed back from PersonalDetails) or Redux profile
  useEffect(() => {
    const locationData = location?.state?.locationData;
    if (locationData?.country) {
      setSelectedCountry(locationData.country);
      if (locationData.city) setSelectedCity(locationData.city);
      return;
    }
    if (!profile) return;
    if (profile.country) {
      const allCountries = Country.getAllCountries();
      const match = allCountries.find(
        (c) => c.isoCode === profile.country || c.name.toLowerCase() === profile.country.toLowerCase()
      );
      if (match) setSelectedCountry(match.isoCode);
    }
    if (profile.city) {
      setSelectedCity(profile.city);
    }
  }, [location?.state?.locationData, profile]);

  // Load cities when country changes
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  useEffect(() => {
    if (selectedCountry) {
      const countryCities = City.getCitiesOfCountry(selectedCountry);
      setCities(countryCities || []);
      // Only reset city on user-driven country changes, not on prefill
      if (!isInitialLoad) {
        setSelectedCity('');
      }
      setIsInitialLoad(false);
    } else {
      setCities([]);
      setSelectedCity('');
    }
  }, [selectedCountry]);

  const handleCountrySelect = (country) => {
    setSelectedCountry(country.isoCode);
    setIsCountryDropdownOpen(false);
    setCountrySearch('');
  };

  const handleCitySelect = (city) => {
    setSelectedCity(city.name);
    setIsCityDropdownOpen(false);
    setCitySearch('');
  };

  const handleContinue = () => {
    const selectedCountryData = countries.find((c) => c.isoCode === selectedCountry);
    const personalData = location?.state?.personalData || {};

    const updateBody = {
      ...personalData,
      country: selectedCountryData?.name || '',
      city: selectedCity,
    };

    setIsSubmitting(true);
    dispatch(
      editProfile(
        updateBody,
        () => {
          setIsSubmitting(false);
          navigate(DASHBOARD_STACK.HOME);
        },
        () => {
          setIsSubmitting(false);
          navigate(DASHBOARD_STACK.HOME);
        },
      ),
    );
  };

  const handleBack = () => {
    navigate(AUTH_STACK.PERSONAL_DETAILS, {
      state: {
        locationData: { country: selectedCountry, city: selectedCity },
      },
    });
  };

  const getSelectedCountryName = () => {
    const country = countries.find((c) => c.isoCode === selectedCountry);
    return country ? country.name : '';
  };

  // Close dropdowns when clicking outside (simple implementation)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setIsCountryDropdownOpen(false);
        setIsCityDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative w-screen h-screen bg-bg-dark flex flex-col items-center justify-center">
      {/* Logo - Top Right */}
      <div className="absolute top-4 right-6 z-10">
        <img className="h-auto w-[120px]" src={clubbieLogo} alt="clubbie logo" />
      </div>

      {/* Main Content */}
      <div className="w-full max-w-xl px-6 mx-auto border-[0.5px] border-app-border py-8 rounded-2xl">
        {/* Title */}
        <div className="text-center mb-6">
          <h1 className="text-4xl font-semibold text-[#FEFCEB] mb-1 font-proxima">Create your account</h1>
          <p className="text-[13px] text-app-text-secondary font-light font-proxima">Enter your details to create your account.</p>
        </div>

        {/* Steps Tracker */}
        <div className="mb-5">
          <StepsTracker currentStep={2} />
        </div>

        {/* Form */}
        <div className="w-full px-4">
          <div className="space-y-4 mb-6">
            {/* Country Dropdown */}
            <div className="dropdown-container flex-col relative">
              <label className="block text-[14px] font-normal text-app-text mb-1.5 ">Country</label>
              <button
                type="button"
                className={`w-full h-12 px-4 bg-app-surface rounded-full border flex items-center justify-between text-[14px] font-proxima transition-colors text-left ${
                  isCountryDropdownOpen ? 'border-[#00E7FF]' : 'border-app-surface'
                }`}
                onClick={() => {
                  setIsCountryDropdownOpen(!isCountryDropdownOpen);
                  setIsCityDropdownOpen(false);
                }}
              >
                <span className={selectedCountry ? 'text-[#FEFCEB]' : 'text-app-text-secondary'}>
                  {getSelectedCountryName() || 'Select a country'}
                </span>
                <span className="text-app-text-secondary text-xs">{isCountryDropdownOpen ? '▲' : '▼'}</span>
              </button>

              {isCountryDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-app-surface border border-app-border rounded-xl z-50 shadow-xl overflow-hidden">
                  <div className="p-2 border-b border-app-border">
                    <input
                      type="text"
                      value={countrySearch}
                      onChange={(e) => setCountrySearch(e.target.value)}
                      placeholder="Search country..."
                      autoFocus
                      className="w-full h-9 px-3 bg-app-bg border border-app-border rounded-full text-[13px] text-[#FEFCEB] placeholder-app-text-secondary font-proxima focus:outline-none focus:border-[#00E7FF] transition-colors"
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto custom-scrollbar">
                    {countries
                      .filter((c) => c.name.toLowerCase().includes(countrySearch.toLowerCase()))
                      .map((country) => (
                        <button
                          key={country.isoCode}
                          type="button"
                          className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 hover:bg-app-hover transition-colors ${
                            selectedCountry === country.isoCode ? 'text-[#00E7FF] bg-app-hover' : 'text-[#FEFCEB]'
                          }`}
                          onClick={() => handleCountrySelect(country)}
                        >
                          <span className="text-lg">{country.flag}</span>
                          <span>{country.name}</span>
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </div>

            {/* City Dropdown */}
            <div className="dropdown-container flex-col relative">
              <label className="block text-[14px] font-normal text-app-text mb-1.5 ">City</label>
              <button
                type="button"
                disabled={!selectedCountry}
                className={`w-full h-12 px-4 bg-app-surface rounded-full border flex items-center justify-between text-[14px] font-proxima transition-colors text-left ${
                  !selectedCountry
                    ? 'opacity-50 cursor-not-allowed border-app-surface'
                    : isCityDropdownOpen
                      ? 'border-[#00E7FF]'
                      : 'border-app-surface'
                }`}
                onClick={() => {
                  if (selectedCountry) {
                    setIsCityDropdownOpen(!isCityDropdownOpen);
                    setIsCountryDropdownOpen(false);
                  }
                }}
              >
                <span className={selectedCity ? 'text-[#FEFCEB]' : 'text-app-text-secondary'}>
                  {selectedCity || (selectedCountry ? 'Select a city' : 'Please select a country first')}
                </span>
                <span className="text-app-text-secondary text-xs">{isCityDropdownOpen ? '▲' : '▼'}</span>
              </button>

              {isCityDropdownOpen && selectedCountry && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-app-surface border border-app-border rounded-xl z-50 shadow-xl overflow-hidden">
                  <div className="p-2 border-b border-app-border">
                    <input
                      type="text"
                      value={citySearch}
                      onChange={(e) => setCitySearch(e.target.value)}
                      placeholder="Search city..."
                      autoFocus
                      className="w-full h-9 px-3 bg-app-bg border border-app-border rounded-full text-[13px] text-[#FEFCEB] placeholder-app-text-secondary font-proxima focus:outline-none focus:border-[#00E7FF] transition-colors"
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto custom-scrollbar">
                    {cities.filter((c) => c.name.toLowerCase().includes(citySearch.toLowerCase())).length > 0 ? (
                      cities
                        .filter((c) => c.name.toLowerCase().includes(citySearch.toLowerCase()))
                        .map((city) => (
                          <button
                            key={city.name}
                            type="button"
                            className={`w-full px-4 py-3 text-left text-sm hover:bg-app-hover transition-colors ${
                              selectedCity === city.name ? 'text-[#00E7FF] bg-app-hover' : 'text-[#FEFCEB]'
                            }`}
                            onClick={() => handleCitySelect(city)}
                          >
                            {city.name}
                          </button>
                        ))
                    ) : (
                      <div className="px-4 py-3 text-sm text-app-text-secondary text-center">
                        No cities found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Info Text */}
            <div className="flex gap-3 items-start mt-2">
              <div className="w-4 h-4 mt-0.5 flex-shrink-0 rounded-full border border-app-text-secondary flex items-center justify-center">
                <span className="text-app-text-secondary text-[10px] font-serif italic">i</span>
              </div>
              <p className="text-xs text-app-text-secondary leading-relaxed">
                Your location may be used to provide you with comparison statistics for you and your teams, depending on your settings.
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <Button
            className="flex-1 w-full h-11 bg-[#00E7FF] mt-5 mb-5 text-[#021529] rounded-full font-medium text-[15px] hover:opacity-50 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed"
            onClick={handleContinue}
            disabled={!selectedCountry || !selectedCity || isSubmitting}
          >
            Continue
          </Button>

          <button
            type="button"
            onClick={handleBack}
            className="w-full text-center text-sm text-app-text-secondary hover:text-light-blue transition-colors"
          >
            Back to the personal details
          </button>
        </div>
      </div>
    </div>
  );
}

export default LocationDetails;
